import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Marker } from 'react-leaflet';
import L, { DivIcon } from 'leaflet';
import { FormState } from 'pages/PMV/Aspects';
import PreviewMiniComp from 'pages/PMV/PreviewMini';
import { DEFAULT_PANEL } from 'pages/PMV/DefaultPanels';
import { IAsset } from 'models/Assets';
import { GREEN, RED } from 'data/colors';

type Props = {
    children: React.ReactNode;
    formState: FormState[];
    eventHandlers?: Record<string, (e: any) => void>;
    asset: IAsset;
};

const MarkerPMV: React.FC<Props> = ({ children, formState, eventHandlers, asset }) => {
    const [currentAspectIndex, setCurrentAspectIndex] = useState<number>(0);
    const containerId = `preview-container-${currentAspectIndex}-${asset.id}`;
    const { latitude, longitude } = asset;
    const pos: [number, number] | null = latitude && longitude ? [latitude, longitude] : null;
    const [position, setPositon] = useState<[number, number] | null>(pos);

    // Recalcular la posición del PMV según el cuerpo A o B
    useEffect(() => {
        // Obtener el último elemento de la descripción
        const cuerpo = asset.description.trim().split(' ').pop();
        if (latitude && longitude) {
            if (cuerpo === 'A') {
                setPositon([latitude, longitude - 0.08]);
            } else if (cuerpo === 'B') {
                setPositon([latitude, longitude + 0.02]);
            }
        }
    }, [asset]);

    const customHtml = `
        <div style="
                border: 3px solid ${formState === DEFAULT_PANEL ? RED : GREEN};
                width: 200px;
                height: 70px;
        ">
            <h3 style="
                width: 100%;
                height: 30%;
                display: flex;
                align-items: center;
                font-size: 12px;
                font-weight: bold;
                margin-bottom: 0px;
                background-color: white;
            ">${asset.description}</h3>
            <div style="
                background-color: black;
                border: 1px solid black;
                width: 100%; /* Ajusta el ancho del pin */
                height: 70%; /* Ajusta la altura del pin */
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                text-align: center; 
                font-size: 8px;">
                <div id="${containerId}"></div>
            </div>
        </div>
    `;

    const customIcon: DivIcon = L.divIcon({
        html: customHtml,
        className: 'custom-div-icon',
        iconSize: [50, 100], // Ajusta el tamaño según sea necesario
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentAspectIndex((prevIndex) => (prevIndex + 1) % formState.length);
        }, 2000);
        return () => clearInterval(interval);
    }, [formState.length]);

    useEffect(() => {
        const container = document.getElementById(containerId);
        if (container) {
            const root = createRoot(container);
            root.render(
                <PreviewMiniComp formState={formState} currentAspectIndex={currentAspectIndex} />
            );
        } else {
            console.error(`Container with id ${containerId} not found`);
        }
    }, [containerId, formState.length, currentAspectIndex, position, asset, formState, children]);

    if (!position) return null;

    return (
        <Marker
            draggable={true}
            position={position}
            icon={customIcon}
            eventHandlers={eventHandlers}
        >{children}</Marker>
    );
};

export default MarkerPMV;
